import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Square } from "lucide-react";

const SLIDES = [
  {
    img: "/tutor-slide-1.jpg",
    kicker: "One-on-One · Online",
    caption: "Math Tutoring — algebra made simple, step by step",
    narration:
      "Welcome to one-on-one math tutoring with Doctor Dennis Louden, where algebra is made simple, step by step.",
  },
  {
    img: "/tutor-slide-4.jpg",
    kicker: "Grades 1–8 · Grades 9–12",
    caption: "Every class builds skills, boosts confidence, and achieves success",
    narration:
      "From elementary school through high school, every class is designed to build skills, boost confidence, and achieve success.",
  },
  {
    img: "/tutor-slide-6.jpg",
    kicker: "From Equations to Confidence",
    caption: "Linear equations, quadratics, graphs, and word problems",
    narration:
      "Linear equations, quadratics, graphs, and word problems — together, we go from equations to confidence.",
  },
  {
    img: "/tutor-slide-8.jpg",
    kicker: "Nursing Algebra Conversions",
    caption: "Dosage calculations, IV flow rates, and real clinical scenarios",
    narration:
      "Nursing students master medication dosage calculations, IV flow rates, and real clinical scenarios.",
  },
  {
    img: "/tutor-slide-10.jpg",
    kicker: "Enroll Today",
    caption: "Take a free assessment, then book your session — $50 each or five for $225",
    narration:
      "Start with a free placement assessment, then book your session — fifty dollars each, or five sessions for two hundred and twenty-five dollars. Enroll today.",
  },
];

const SLIDE_MS = 7000;

export default function TutoringShowcase() {
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  const speak = (text) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1;
    window.speechSynthesis.speak(u);
  };

  const stop = () => {
    clearInterval(timer.current);
    timer.current = null;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setPlaying(false);
  };

  const play = () => {
    setIdx(0);
    setPlaying(true);
    speak(SLIDES[0].narration);
    let i = 0;
    timer.current = setInterval(() => {
      i += 1;
      if (i >= SLIDES.length) {
        stop();
        return;
      }
      setIdx(i);
      speak(SLIDES[i].narration);
    }, SLIDE_MS);
  };

  useEffect(() => () => {
    clearInterval(timer.current);
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  const slide = SLIDES[idx];

  return (
    <section data-testid="tutor-showcase" className="mt-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#A78BFA]">Watch &amp; Listen</span>
          <h2 className="mt-3 font-display text-4xl md:text-6xl uppercase tracking-tight text-white">
            About Our Math Classes
          </h2>
        </div>
        {playing ? (
          <button onClick={stop} data-testid="showcase-stop-btn"
            className="magnetic-btn inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black">
            <Square size={13} /> Stop
          </button>
        ) : (
          <button onClick={play} data-testid="showcase-play-btn"
            className="magnetic-btn inline-flex items-center gap-2 rounded-full bg-[#7C3AED] px-6 py-3 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black">
            <Play size={13} /> Play with Voice
          </button>
        )}
      </div>

      <div className="relative mt-8 overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(124,58,237,0.35)]">
        <div className="relative aspect-video w-full">
          <AnimatePresence mode="wait">
            <motion.img
              key={idx}
              src={slide.img}
              alt={slide.caption}
              data-testid="showcase-image"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-6 md:p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]" data-testid="showcase-kicker">
                  {slide.kicker}
                </div>
                <div className="mt-2 max-w-3xl text-base font-medium text-white md:text-lg" data-testid="showcase-caption">
                  {slide.caption}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 border-t border-white/10 bg-[#0D0D12] py-3">
          {SLIDES.map((s, i) => (
            <span key={s.img} data-testid={`showcase-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx && playing ? "w-8 bg-[#7C3AED]" : "w-1.5 bg-white/25"}`} />
          ))}
        </div>
      </div>
      <p className="mt-3 text-xs text-white/40">
        Press Play with Voice — each picture fades in while a voice tells you about that class.
      </p>
    </section>
  );
}
